'use server'

import { apiRequest, ApiResponse } from '../request'

export type WhatsappBroadcastStatus = 'DRAFT' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'CANCELED'
export type WhatsappBroadcastRecipientStatus = 'PENDING' | 'SENT' | 'SKIPPED' | 'FAILED'

export interface WhatsappBroadcastTemplateRef {
    id: number
    name: string
    friendlyName: string
    bodyText?: string
}

export interface WhatsappBroadcast {
    id: number
    organizationId: number
    companyId: number
    name: string
    departmentId: number
    templateId: number
    fallbackTemplateId: number | null
    /** Texto no lugar do nome para quem não tem nome. */
    nameFallback: string | null
    status: WhatsappBroadcastStatus
    pausedReason: string | null
    limitSharePercent: number
    dailyCap: number | null
    windowStartHour: number
    windowEndHour: number
    sendOnWeekends: boolean
    metaMessagingLimit: string | null
    metaQualityRating: string | null
    metaCheckedAt: string | null
    startedAt: string | null
    completedAt: string | null
    createdAt: string
    department?: { id: number; name: string; whatsappNumber: string | null }
    template?: WhatsappBroadcastTemplateRef
    fallbackTemplate?: WhatsappBroadcastTemplateRef | null
    _count?: { recipients: number }
}

export interface WhatsappBroadcastStats {
    total: number
    pending: number
    sent: number
    delivered: number
    read: number
    replied: number
    failed: number
    skipped: number
    sentLast24h: number
}

export interface WhatsappBroadcastMetaLimit {
    messagingLimit: string | null
    limit: number
    qualityRating: string | null
    usedLast24h: number
}

export interface WhatsappBroadcastRecipient {
    id: number
    phoneNumber: string
    name: string | null
    firstName: string | null
    lastActivityAt: string | null
    status: WhatsappBroadcastRecipientStatus
    skipReason: string | null
    sentAt: string | null
    deliveredAt: string | null
    readAt: string | null
    repliedAt: string | null
    failedAt: string | null
    errorCode: number | null
    errorTitle: string | null
}

export interface WhatsappBroadcastFormData {
    name: string
    departmentId: number
    templateId: number
    fallbackTemplateId?: number | null
    nameFallback?: string | null
    limitSharePercent?: number
    dailyCap?: number | null
    windowStartHour?: number
    windowEndHour?: number
    sendOnWeekends?: boolean
}

export interface WhatsappBroadcastRecipientInput {
    phone: string
    name?: string | null
    lastActivityAt?: string | null
    externalRef?: string | null
}

export const WHATSAPP_BROADCAST_STATUS_LABELS: Record<WhatsappBroadcastStatus, string> = {
    DRAFT: 'Rascunho',
    RUNNING: 'Enviando',
    PAUSED: 'Pausado',
    COMPLETED: 'Concluído',
    CANCELED: 'Cancelado',
}

export const WHATSAPP_BROADCAST_STATUS_COLORS: Record<WhatsappBroadcastStatus, string> = {
    DRAFT: 'badge-ghost',
    RUNNING: 'badge-info',
    PAUSED: 'badge-warning',
    COMPLETED: 'badge-success',
    CANCELED: 'badge-neutral',
}

export const WHATSAPP_BROADCAST_RECIPIENT_STATUS_LABELS: Record<WhatsappBroadcastRecipientStatus, string> = {
    PENDING: 'Na fila',
    SENT: 'Enviado',
    SKIPPED: 'Pulado',
    FAILED: 'Falhou',
}

export const META_LIMIT_LABELS: Record<string, string> = {
    TIER_250: '250 contatos / 24h',
    TIER_1K: '1.000 contatos / 24h',
    TIER_2K: '2.000 contatos / 24h',
    TIER_10K: '10.000 contatos / 24h',
    TIER_100K: '100.000 contatos / 24h',
    TIER_UNLIMITED: 'Ilimitado',
}

export const META_QUALITY_LABELS: Record<string, { label: string; color: string }> = {
    GREEN: { label: 'Alta', color: 'badge-success' },
    YELLOW: { label: 'Média', color: 'badge-warning' },
    RED: { label: 'Baixa', color: 'badge-error' },
    UNKNOWN: { label: 'Sem avaliação', color: 'badge-ghost' },
}

const BASE = 'messaging/whatsapp-broadcasts'

export async function getWhatsappBroadcasts(): Promise<ApiResponse<WhatsappBroadcast[]>> {
    return apiRequest<WhatsappBroadcast[]>(BASE, {})
}

export async function getWhatsappBroadcast(id: number): Promise<ApiResponse<WhatsappBroadcast>> {
    return apiRequest<WhatsappBroadcast>(`${BASE}/${id}`, {})
}

export async function getWhatsappBroadcastStats(id: number): Promise<ApiResponse<WhatsappBroadcastStats>> {
    return apiRequest<WhatsappBroadcastStats>(`${BASE}/${id}/stats`, {})
}

export async function getWhatsappBroadcastRecipients(
    id: number,
    params: { status?: WhatsappBroadcastRecipientStatus; page?: number; limit?: number },
): Promise<ApiResponse<{ items: WhatsappBroadcastRecipient[]; total: number; page: number; limit: number }>> {
    return apiRequest(`${BASE}/${id}/recipients`, { params })
}

export async function getWhatsappBroadcastMetaLimit(
    departmentId: number,
): Promise<ApiResponse<WhatsappBroadcastMetaLimit>> {
    return apiRequest<WhatsappBroadcastMetaLimit>(`${BASE}/meta-limit`, { params: { departmentId } })
}

export async function createWhatsappBroadcast(data: WhatsappBroadcastFormData): Promise<ApiResponse<WhatsappBroadcast>> {
    return apiRequest<WhatsappBroadcast>(BASE, { data }, 'post')
}

export async function updateWhatsappBroadcast(
    id: number,
    data: Partial<WhatsappBroadcastFormData>,
): Promise<ApiResponse<WhatsappBroadcast>> {
    return apiRequest<WhatsappBroadcast>(`${BASE}/${id}`, { data }, 'patch')
}

export async function importWhatsappBroadcastRecipients(
    id: number,
    recipients: WhatsappBroadcastRecipientInput[],
): Promise<ApiResponse<{ received: number; imported: number; duplicates: number; invalid: number }>> {
    return apiRequest(`${BASE}/${id}/recipients`, { data: { recipients } }, 'post')
}

export async function startWhatsappBroadcast(id: number): Promise<ApiResponse<WhatsappBroadcast>> {
    return apiRequest<WhatsappBroadcast>(`${BASE}/${id}/start`, {}, 'post')
}

export async function pauseWhatsappBroadcast(id: number): Promise<ApiResponse<WhatsappBroadcast>> {
    return apiRequest<WhatsappBroadcast>(`${BASE}/${id}/pause`, {}, 'post')
}

export async function cancelWhatsappBroadcast(id: number): Promise<ApiResponse<WhatsappBroadcast>> {
    return apiRequest<WhatsappBroadcast>(`${BASE}/${id}/cancel`, {}, 'post')
}

export async function deleteWhatsappBroadcast(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(`${BASE}/${id}`, {}, 'delete')
}
